/**
 * People hub — รวมงานทุกอย่างที่เกี่ยวกับพนักงานเข้าไว้ที่เดียว.
 *
 * Tabs:
 *   - ทั้งหมด         → HREmployeesContent (รายชื่อ + แก้ไข)
 *   - เพิ่มใหม่        → OwnerOnboardContent
 *   - ลาออก/เลิกจ้าง  → OwnerTerminationContent
 *   - ตามเก็บประวัติ   → HRFollowupContent
 *   - คำขอแก้ข้อมูล    → OwnerPendingChangesContent (เดิมอยู่ใน "ตั้งค่า")
 *
 * URL hash (#all|#onboard|#termination|#followup|#changes) preserves
 * deep links from dashboard / briefing.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { HubHeader, PillTabs } from '../../components/layout/HubHeader';
import { useAuthStore } from '../../lib/store';
import { HREmployeesContent } from './Employees';
import { OwnerOnboardContent } from '../owner/Onboard';
import { OwnerTerminationContent } from '../owner/Termination';
import { HRFollowupContent } from './Followup';
import { OwnerPendingChangesContent } from '../owner/PendingChanges';

type PeopleTab = 'all' | 'onboard' | 'termination' | 'followup' | 'changes';
const VALID_TABS: PeopleTab[] = ['all', 'onboard', 'termination', 'followup', 'changes'];

export function People() {
  const role = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const isOwner = role === 'OWNER';

  const [tab, setTab] = useState<PeopleTab>(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '') as PeopleTab;
    return VALID_TABS.includes(h) ? h : 'all';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = `#${tab}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + next);
    }
  }, [tab]);
  // sync tab จาก #hash เมื่อ navigate ผ่าน sidebar (เมนูย่อย)
  const __loc = useLocation();
  useEffect(() => {
    const h = __loc.hash.replace('#', '') as PeopleTab;
    if (VALID_TABS.includes(h)) setTab(h);
  }, [__loc.hash]);


  // Order: ใช้บ่อยซ้าย, setup/เหตุการณ์รารๆ ขวา (Owner-design 2026-05-28)
  const tabs: Array<{ key: PeopleTab; label: string; visible: boolean }> = [
    { key: 'all', label: 'ทั้งหมด', visible: true },
    { key: 'changes', label: 'คำขอแก้ข้อมูล', visible: isOwner },
    { key: 'followup', label: 'ตามเก็บประวัติ', visible: true },
    { key: 'onboard', label: 'เพิ่มใหม่', visible: true },
    { key: 'termination', label: 'ลาออก/เลิกจ้าง', visible: true },
  ];
  const visible = tabs.filter((t) => t.visible);
  const active = visible.some((t) => t.key === tab) ? tab : 'all';
  const activeLabel = visible.find((t) => t.key === active)?.label ?? 'พนักงาน';
  const roleLabel = role === 'OWNER' ? 'เจ้าของ' : role === 'HR' ? 'ฝ่ายบุคคล' : role === 'MANAGER' ? 'ผู้จัดการ' : role;

  return (
    <AdminLayout title="พนักงาน">
      <HubHeader
        section="พนักงาน"
        title={activeLabel}
        roleLabel={roleLabel}
        action={active !== 'onboard'
          ? <button onClick={() => setTab('onboard')} className="btn-cta btn-slim">+ เพิ่มพนักงาน</button>
          : undefined}
      />
      <PillTabs tabs={visible} active={active} onChange={setTab} />

      {active === 'all' && <HREmployeesContent />}
      {active === 'onboard' && <OwnerOnboardContent />}
      {active === 'termination' && <OwnerTerminationContent />}
      {active === 'followup' && <HRFollowupContent />}
      {active === 'changes' && <OwnerPendingChangesContent />}
    </AdminLayout>
  );
}
